import { api } from '@goaltree/backend/convex/_generated/api'
import {
  BADGES,
  BADGE_BY_ID,
  BADGE_TIERS,
  type BadgeDef,
  CANOPIES,
  type CanopyDef,
  MAX_SHOWCASE,
  TIER_COLORS,
  badgeProgress,
  canopyUnlocked,
  showcaseBadges,
} from '@goaltree/backend/convex/lib/progression'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { type TFunction } from 'i18next'
import { Ban, Lock, Pin } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { BadgeMedal } from '@/components/badge-medal'
import { CanopyRing } from '@/components/canopy-avatar'

type Growth = NonNullable<FunctionReturnType<typeof api.progression.getGrowth>>

function patchMyGrowth(store: OptimisticLocalStore, fn: (g: Growth) => Growth) {
  for (const { args, value } of store.getAllQueries(api.progression.getGrowth)) {
    if (value?.isMe) store.setQuery(api.progression.getGrowth, args, fn(value))
  }
}

function Label({ children, meta }: { children: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/40">
      {children}
      {meta && (
        <span className="text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
          {meta}
        </span>
      )}
    </div>
  )
}

// Profile-sheet strip: showcase badges, opening the full grid.
export function GrowthSection({ userId }: { userId?: string }) {
  const { t } = useTranslation()
  const growth = useQuery(api.progression.getGrowth, userId ? { userId } : {})
  const [open, setOpen] = useState(false)
  if (!growth || (!growth.isMe && growth.badges.length === 0)) return null
  const shown = showcaseBadges(growth.badges, growth.showcase)
  return (
    <div className="space-y-2">
      <Label
        meta={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="cursor-pointer font-semibold text-primary-text hover:underline"
          >
            {t('progression.earned', { n: growth.badges.length, total: BADGES.length })} ›
          </button>
        }
      >
        {t('progression.section')}
      </Label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('progression.viewAll')}
        className="flex w-full cursor-pointer flex-wrap items-center gap-2.5 rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
      >
        {shown.length ? (
          shown.map((id) => <BadgeMedal key={id} id={id} size="sm" />)
        ) : (
          <span className="min-w-0 text-xs text-muted-foreground">
            {growth.isMe ? t('progression.empty') : t('progression.emptyOther')}
          </span>
        )}
      </button>
      <BadgesDialog userId={userId} open={open} onOpenChange={setOpen} />
    </div>
  )
}

export function BadgesDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const growth = useQuery(api.progression.getGrowth, open ? (userId ? { userId } : {}) : 'skip')
  const isMe = !!growth?.isMe
  const progress = useQuery(api.progression.getMyBadgeProgress, open && isMe ? {} : 'skip')
  const setShowcase = useMutation(api.progression.setShowcase).withOptimisticUpdate(
    (store, { badgeIds }) =>
      patchMyGrowth(store, (g) => {
        const owned = new Set(g.badges.map((b) => b.id))
        return { ...g, showcase: [...new Set(badgeIds)].filter((id) => owned.has(id)) }
      }),
  )
  const owned = new Map(growth?.badges.map((b) => [b.id, b.unlockedAt]))

  function togglePin(id: string) {
    if (!growth) return
    const pinned = growth.showcase
    const next = pinned.includes(id) ? pinned.filter((p) => p !== id) : [...pinned, id]
    if (next.length > MAX_SHOWCASE) {
      toast(t('progression.pinFull'))
      return
    }
    setShowcase({ badgeIds: next }).catch(() => toast.error(t('progression.pinFailed')))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <div className="space-y-1 border-b border-border/60 px-5 pt-5 pb-4">
          <DialogTitle className="text-lg font-extrabold tracking-tight">
            {t('progression.badges')}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {t('progression.earned', { n: owned.size, total: BADGES.length })}
            {isMe && ` · ${t('progression.pinHint')}`}
          </p>
        </div>
        <div className="space-y-6 overflow-y-auto px-5 py-5">
          {isMe && growth && <CanopyPicker growth={growth} />}
          {BADGE_TIERS.map((tier) => (
            <section key={tier} className="space-y-2.5">
              <Label>
                <span className="flex items-center gap-1.5">
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: TIER_COLORS[tier] }}
                  />
                  {t(`progression.tier.${tier}`)}
                </span>
              </Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {BADGES.filter((b) => b.tier === tier).map((def) => (
                  <BadgeTile
                    key={def.id}
                    def={def}
                    unlockedAt={owned.get(def.id)}
                    pinned={!!growth?.showcase.includes(def.id)}
                    progress={progress}
                    onPin={isMe && owned.has(def.id) ? () => togglePin(def.id) : undefined}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function BadgeTile({
  def,
  unlockedAt,
  pinned,
  progress,
  onPin,
}: {
  def: BadgeDef
  unlockedAt: number | undefined
  pinned: boolean
  progress: Record<string, number> | undefined
  onPin?: () => void
}) {
  const { t, i18n } = useTranslation()
  const locked = unlockedAt === undefined
  const hint = locked && progress ? badgeProgress(def, progress) : null
  const Tag = onPin ? 'button' : 'div'
  return (
    <Tag
      {...(onPin ? { type: 'button' as const, onClick: onPin, 'aria-pressed': pinned } : {})}
      title={onPin ? (pinned ? t('progression.unpin') : t('progression.pin')) : undefined}
      className={cn(
        'relative flex flex-col items-center gap-1.5 rounded-xl border border-border bg-muted/15 px-2.5 pt-3 pb-2.5 text-center',
        onPin && 'cursor-pointer transition-colors hover:bg-muted/40',
        pinned && 'border-primary/60 bg-primary/5',
      )}
    >
      {pinned && <Pin className="absolute top-2 right-2 size-3 text-primary-text" />}
      <BadgeMedal id={def.id} locked={locked} />
      <span
        className={cn(
          'text-[12.5px] font-bold leading-tight',
          locked ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {t(`progression.badge.${def.id}.name`)}
      </span>
      <span className="line-clamp-2 text-[10.5px] leading-snug text-muted-foreground">
        {t(`progression.badge.${def.id}.desc`)}
      </span>
      {hint && (
        <span className="mt-0.5 flex w-full items-center gap-1.5">
          <span className="h-[3px] flex-1 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full"
              style={{ width: `${hint.ratio * 100}%`, background: TIER_COLORS[def.tier] }}
            />
          </span>
          <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
            {t('progression.progress', { value: hint.value, goal: hint.goal })}
          </span>
        </span>
      )}
      {!locked && (
        <span className="font-mono text-[10px] text-muted-foreground/80">
          {t('progression.earnedOn', {
            date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
              unlockedAt,
            ),
          })}
        </span>
      )}
    </Tag>
  )
}

export function canopyRequirement(def: CanopyDef, t: TFunction) {
  return 'badges' in def.unlock
    ? t('progression.canopyUnlock.badges', { count: def.unlock.badges })
    : t('progression.canopyUnlock.badge', {
        name: t(`progression.badge.${def.unlock.badge}.name`),
      })
}

function CanopyPicker({ growth }: { growth: Growth }) {
  const { t } = useTranslation()
  const setCanopy = useMutation(api.progression.setCanopy).withOptimisticUpdate(
    (store, { canopy }) => patchMyGrowth(store, (g) => ({ ...g, canopy })),
  )
  const ownedIds = growth.badges.map((b) => b.id).filter((id) => BADGE_BY_ID[id])
  const pick = (canopy: string | null) =>
    setCanopy({ canopy }).catch(() => toast.error(t('progression.canopyFailed')))

  return (
    <section className="space-y-2.5">
      <Label>{t('progression.canopy')}</Label>
      <p className="text-xs text-muted-foreground">{t('progression.canopyHint')}</p>
      <div className="grid grid-cols-4 gap-x-2 gap-y-3 sm:grid-cols-7">
        <CanopyOption
          label={t('progression.canopyNone')}
          active={growth.canopy === null}
          onPick={() => void pick(null)}
        />
        {CANOPIES.map((c) => {
          const unlocked = canopyUnlocked(c, ownedIds)
          return (
            <CanopyOption
              key={c.id}
              canopy={c.id}
              label={t(`progression.canopyName.${c.id}`)}
              requirement={unlocked ? undefined : canopyRequirement(c, t)}
              active={growth.canopy === c.id}
              onPick={unlocked ? () => void pick(c.id) : undefined}
            />
          )
        })}
      </div>
    </section>
  )
}

function CanopyOption({
  canopy,
  label,
  requirement,
  active,
  onPick,
}: {
  canopy?: string
  label: string
  requirement?: string
  active: boolean
  onPick?: () => void
}) {
  return (
    <button
      type="button"
      disabled={!onPick}
      onClick={onPick}
      aria-pressed={active}
      title={requirement ?? label}
      className="flex cursor-pointer flex-col items-center gap-2 disabled:cursor-default"
    >
      <span
        className={cn(
          'relative mt-1.5 flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground',
          active && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
          requirement && 'opacity-40',
        )}
      >
        {canopy && <CanopyRing canopy={canopy} />}
        {requirement ? <Lock className="size-3.5" /> : !canopy && <Ban className="size-3.5" />}
      </span>
      <span className="text-[10.5px] font-semibold leading-tight text-foreground">{label}</span>
      {requirement && (
        <span className="-mt-1 text-[9.5px] leading-tight text-muted-foreground">
          {requirement}
        </span>
      )}
    </button>
  )
}

// Settings → Profile: badge count and canopy, opening the grid + canopy picker.
export function GrowthSettingsSummary({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation()
  const growth = useQuery(api.progression.getGrowth, {})
  if (!growth) return null
  const shown = showcaseBadges(growth.badges, growth.showcase)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{t('progression.section')}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {t('progression.earned', { n: growth.badges.length, total: BADGES.length })}
          {growth.canopy && ` · ${t(`progression.canopyName.${growth.canopy}`)}`}
        </p>
      </div>
      <span className="flex shrink-0 -space-x-1.5">
        {shown.slice(0, 3).map((id) => (
          <BadgeMedal key={id} id={id} size="sm" />
        ))}
      </span>
      <span className="shrink-0 text-muted-foreground">›</span>
    </button>
  )
}
